import { useState } from "react";
import {
  Mail,
  Linkedin,
  MapPin,
  Clock,
  ArrowUpRight,
  CheckCircle2,
  Lock,
  Globe2,
  Scale,
} from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { IMAGERY } from "../lib/imagery";
import { submitLead } from "../lib/api";
import { toast } from "sonner";
import { accent } from "../lib/accent";
import { trackEvent } from "../lib/analytics";

const initial = {
  full_name: "",
  email: "",
  phone: "",
  company: "",
  reason: "",
};

function Field({ label, error, children, testId }) {
  return (
    <div data-testid={testId}>
      <label className="block font-sans text-[11px] uppercase tracking-[0.22em] text-white/60 font-medium mb-2">
        {label}
      </label>
      {children}
      {error && (
        <p className="mt-2 text-xs text-red-400 font-sans uppercase tracking-[0.18em]">
          {error}
        </p>
      )}
    </div>
  );
}

export default function Contact() {
  const { t, locale } = useLang();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = t.contact.f_required;
    if (!form.email.trim()) e.email = t.contact.f_required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = t.contact.f_invalid_email;
    if (!form.phone.trim()) e.phone = t.contact.f_required;
    if (!form.company.trim()) e.company = t.contact.f_required;
    if (!form.reason.trim() || form.reason.trim().length < 5)
      e.reason = t.contact.f_required;
    return e;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length) return;

    setSubmitting(true);
    try {
      await submitLead({ ...form, locale });
      trackEvent("lead_form_submit", {
        form_location: "contact_page",
        locale,
        has_company: Boolean(form.company),
        has_phone: Boolean(form.phone),
      });
      setSuccess(true);
      setForm(initial);
      toast.success(t.contact.f_success_title);
    } catch (err) {
      trackEvent("lead_form_error", {
        form_location: "contact_page",
        locale,
      });
      toast.error(t.contact.f_error);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-0 border-b border-white/15 px-0 py-3.5 font-sans text-[15px] text-white placeholder:text-white/30 focus:outline-none focus:border-champagne transition-colors duration-300";

  return (
    <>
      {/* HERO */}
      <section
        data-testid="contact-hero"
        className="relative -mt-20 pt-40 pb-20 md:pb-28 bg-obsidian text-white overflow-hidden"
      >
        <img
          src={IMAGERY.cranes}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="container-x relative z-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-9 animate-fade-in-up">
            <p className="eyebrow-light">{t.contact.eyebrow}</p>
            <h1 className="display-h1 mt-6 max-w-[16ch] text-white">
              {accent(t.contact.title)}
            </h1>
            <div className="section-divider mt-10 mr-auto ml-0" />
            <p className="font-sans text-lg md:text-xl lg:text-2xl text-white/85 mt-8 max-w-3xl leading-relaxed font-light animate-fade-in delay-200">
              {t.contact.body}
            </p>
          </div>
        </div>
      </section>

      {/* MAIN CONTACT SECTION */}
      <section
        className="relative py-24 md:py-32 overflow-hidden"
        style={{ backgroundColor: "#0A1020" }}
      >
        {/* faint architectural backdrop */}
        <img
          src="/value-proposition-backdrop.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.18]"
          style={{ filter: "saturate(0.7) hue-rotate(200deg)" }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,16,32,0.85) 0%, rgba(10,16,32,0.55) 50%, rgba(10,16,32,0.95) 100%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 grid-pattern opacity-30 pointer-events-none"
        />

        <div className="container-x relative z-10">
          <div className="grid grid-cols-12 gap-8 lg:gap-12">
            {/* LEFT: FORM PANEL */}
            <div className="col-span-12 lg:col-span-7">
              <div
                className="relative p-8 md:p-12 lg:p-14"
                style={{
                  background: "rgba(20, 24, 36, 0.65)",
                  backdropFilter: "blur(16px) saturate(1.1)",
                  WebkitBackdropFilter: "blur(16px) saturate(1.1)",
                  border: "1px solid rgba(148, 163, 184, 0.12)",
                  borderRadius: "2px",
                }}
              >
                <p className="eyebrow">{t.contact.formEyebrow}</p>
                <h2 className="display-h2 mt-4 text-white max-w-[18ch]">
                  {accent(t.contact.formTitle)}
                </h2>
                <div className="section-divider mt-8 mr-auto ml-0" />

                {success ? (
                  <div
                    data-testid="contact-success"
                    className="mt-12 border border-champagne/40 bg-obsidian/60 p-10 flex gap-5"
                  >
                    <CheckCircle2 className="text-champagne mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-serif text-2xl text-white">
                        {t.contact.f_success_title}
                      </p>
                      <p className="font-sans text-white/75 mt-3 leading-relaxed font-light max-w-md">
                        {t.contact.f_success_body}
                      </p>
                      <button
                        type="button"
                        onClick={() => setSuccess(false)}
                        data-testid="contact-success-reset"
                        className="mt-6 font-sans text-[11px] uppercase tracking-[0.22em] text-champagne hover:text-white transition-colors font-medium"
                      >
                        {t.common.back}
                      </button>
                    </div>
                  </div>
                ) : (
                  <form
                    onSubmit={onSubmit}
                    data-testid="contact-form"
                    noValidate
                    className="mt-12 space-y-10"
                  >
                    <fieldset className="space-y-7">
                      <legend className="eyebrow mb-6 block">
                        {t.contact.f_personal}
                      </legend>
                      <Field
                        label={t.contact.f_fullName}
                        error={errors.full_name}
                      >
                        <input
                          data-testid="input-fullname"
                          type="text"
                          autoComplete="name"
                          placeholder={t.contact.f_fullNamePh}
                          value={form.full_name}
                          onChange={onChange("full_name")}
                          className={inputClass}
                        />
                      </Field>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                        <Field
                          label={t.contact.f_email}
                          error={errors.email}
                        >
                          <input
                            data-testid="input-email"
                            type="email"
                            autoComplete="email"
                            placeholder={t.contact.f_emailPh}
                            value={form.email}
                            onChange={onChange("email")}
                            className={inputClass}
                          />
                        </Field>
                        <Field
                          label={t.contact.f_phone}
                          error={errors.phone}
                        >
                          <input
                            data-testid="input-phone"
                            type="tel"
                            autoComplete="tel"
                            placeholder={t.contact.f_phonePh}
                            value={form.phone}
                            onChange={onChange("phone")}
                            className={inputClass}
                          />
                        </Field>
                      </div>
                    </fieldset>

                    <fieldset>
                      <legend className="eyebrow mb-6 block">
                        {t.contact.f_company}
                      </legend>
                      <Field
                        label={t.contact.f_companyName}
                        error={errors.company}
                      >
                        <input
                          data-testid="input-company"
                          type="text"
                          autoComplete="organization"
                          placeholder={t.contact.f_companyPh}
                          value={form.company}
                          onChange={onChange("company")}
                          className={inputClass}
                        />
                      </Field>
                    </fieldset>

                    <fieldset>
                      <legend className="eyebrow mb-6 block">
                        {t.contact.f_intent}
                      </legend>
                      <textarea
                        data-testid="input-reason"
                        rows={5}
                        placeholder={t.contact.f_reasonPh}
                        value={form.reason}
                        onChange={onChange("reason")}
                        className={`${inputClass} resize-none`}
                      />
                      {errors.reason && (
                        <p className="mt-2 text-xs text-red-400 font-sans uppercase tracking-[0.18em]">
                          {errors.reason}
                        </p>
                      )}
                    </fieldset>

                    <div className="space-y-4 pt-2">
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="submit-button"
                        className="group gold-btn disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {submitting ? "..." : t.contact.f_submit}
                        <ArrowUpRight size={16} />
                      </button>
                      <p className="font-sans text-[12px] text-white/50 tracking-wide">
                        {t.contact.f_response}
                      </p>
                    </div>
                  </form>
                )}
              </div>
            </div>

            {/* RIGHT: VISUAL + DIRECT CHANNELS */}
            <aside
              data-testid="contact-aside"
              className="col-span-12 lg:col-span-5 space-y-8 lg:sticky lg:top-32 self-start"
            >
              {/* Cinematic photo */}
              <div className="relative aspect-[5/6] overflow-hidden">
                <img
                  src="/value-proposition-backdrop.jpg"
                  alt="Cinematic architectural render of strategic intelligence"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ filter: "brightness(0.85) contrast(1.05)" }}
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(10,16,32,0.20) 0%, rgba(10,16,32,0.55) 60%, rgba(10,16,32,0.95) 100%)",
                  }}
                />
                <div className="absolute inset-0 ring-1 ring-inset ring-champagne/15" />
                <div className="absolute top-5 left-5 right-5 flex items-center gap-3">
                  <span className="block w-8 h-px bg-champagne" />
                  <span className="font-sans text-[10px] uppercase tracking-[0.28em] text-white font-medium">
                    Principal Desk
                  </span>
                </div>
                <div className="absolute bottom-7 left-7 right-7">
                  <p className="font-serif italic text-xl md:text-2xl text-white leading-snug">
                    {t.contact.asideQuote}
                  </p>
                  <span className="block w-10 h-px bg-champagne mt-5" />
                </div>
              </div>

              {/* Direct channels card */}
              <div
                className="p-7 lg:p-8"
                style={{
                  background: "rgba(20, 24, 36, 0.65)",
                  backdropFilter: "blur(16px) saturate(1.1)",
                  WebkitBackdropFilter: "blur(16px) saturate(1.1)",
                  border: "1px solid rgba(148, 163, 184, 0.12)",
                  borderRadius: "2px",
                }}
              >
                <p className="eyebrow">{t.contact.directEyebrow}</p>
                <h3 className="font-serif text-xl lg:text-2xl text-white mt-3">
                  {accent(t.contact.directTitle)}
                </h3>

                <ul className="mt-8 divide-y divide-white/8">
                  {[
                    {
                      icon: Mail,
                      label: t.contact.emailLabel,
                      value: "contact@nivneconsulting.com",
                      href: "mailto:contact@nivneconsulting.com",
                      tid: "contact-email-link",
                    },
                    {
                      icon: Linkedin,
                      label: t.contact.linkedinLabel,
                      value: "/nivne-consulting-group",
                      href: "https://www.linkedin.com/company/nivne-consulting-group",
                      external: true,
                      tid: "contact-linkedin-link",
                    },
                    {
                      icon: MapPin,
                      label: t.contact.locationsLabel,
                      value: t.contact.locationsValue,
                    },
                    {
                      icon: Clock,
                      label: t.contact.hoursLabel,
                      value: t.contact.hoursValue,
                    },
                  ].map((row, i) => {
                    const Icon = row.icon;
                    const inner = (
                      <>
                        <span className="w-9 h-9 flex-shrink-0 inline-flex items-center justify-center border border-champagne/30 bg-champagne/5">
                          <Icon size={14} className="text-champagne" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block font-sans text-[10px] uppercase tracking-[0.22em] text-white/55 font-medium">
                            {row.label}
                          </span>
                          <span className="block font-sans text-[15px] text-white truncate mt-1">
                            {row.value}
                          </span>
                        </span>
                      </>
                    );
                    return (
                      <li key={i} className="py-4">
                        {row.href ? (
                          <a
                            href={row.href}
                            target={row.external ? "_blank" : undefined}
                            rel={row.external ? "noopener noreferrer" : undefined}
                            data-testid={row.tid}
                            className="flex items-center gap-4 group hover:text-champagne transition-colors"
                          >
                            {inner}
                          </a>
                        ) : (
                          <div className="flex items-center gap-4">{inner}</div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* TRUST INDICATORS STRIP */}
      <section
        data-testid="contact-trust-strip"
        className="relative bg-obsidian-700 border-y border-white/5 overflow-hidden"
      >
        <div className="container-x py-14 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/8">
            {[
              {
                icon: Lock,
                label: t.contact.trust1Label,
                value: t.contact.trust1Value,
              },
              {
                icon: Clock,
                label: t.contact.trust2Label,
                value: t.contact.trust2Value,
              },
              {
                icon: Scale,
                label: t.contact.trust3Label,
                value: t.contact.trust3Value,
              },
              {
                icon: Globe2,
                label: t.contact.trust4Label,
                value: t.contact.trust4Value,
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  data-testid={`trust-card-${i + 1}`}
                  className="bg-obsidian-700 px-6 lg:px-8 py-6 flex items-center gap-4"
                >
                  <span className="w-10 h-10 inline-flex items-center justify-center border border-champagne/30 bg-champagne/5 flex-shrink-0">
                    <Icon size={16} className="text-champagne" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-white/55 font-medium">
                      {item.label}
                    </p>
                    <p className="font-serif text-base lg:text-lg text-white mt-1 leading-tight">
                      {item.value}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
